import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaEnvelope, FaKey, FaLock, FaEye, FaEyeSlash, FaArrowLeft } from 'react-icons/fa';
import client from '../api/client';
import { toast } from 'react-toastify';
import '../App.css';

const OTP_EXPIRY_SECONDS = 600;

function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();

  const getInitialUserType = () => {
    if (location.pathname.includes('professional')) return 'professional';
    if (location.pathname.includes('hospital')) return 'hospital';
    return 'admin';
  };

  const [formData, setFormData] = useState({
    userType: getInitialUserType(),
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpTimer, setOtpTimer] = useState(OTP_EXPIRY_SECONDS);
  const [otpMessage, setOtpMessage] = useState({ show: false, text: '', type: 'info' });
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const timerRef = useRef(null);

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  useEffect(() => {
    if (step !== 2 || !otpSent) return undefined;

    timerRef.current = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setOtpMessage({
            show: true,
            text: 'OTP has expired. Please request a new OTP.',
            type: 'danger',
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [step, otpSent]);

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'otp') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6);
      setFormData((prev) => ({ ...prev, otp: numericValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (name === 'email') {
      setOtpSent(false);
      setOtpVerified(false);
      setFormData((prev) => ({ ...prev, otp: '' }));
    }

    if (name === 'otp' && otpMessage.show) {
      setOtpMessage({ show: false, text: '', type: 'info' });
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();

    if (!formData.email) {
      toast.error('Please enter your email address');
      return;
    }

    if (!validateEmail(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSendingOtp(true);
    setOtpMessage({ show: false, text: '', type: 'info' });
    try {
      const response = await client.get(
        `/api/otp/set-otp?emailID=${encodeURIComponent(formData.email.trim())}`,
      );
      const { success, message, data } = response.data || {};

      if (!success) {
        toast.error(message || 'Failed to send OTP');
        return;
      }

      if (data?.userID === 0) {
        toast.error(data?.message || message || 'OTP could not be sent');
        return;
      }

      toast.success(data?.message || message || 'OTP sent successfully to your email');
      setOtpSent(true);
      setOtpVerified(false);
      setOtpTimer(OTP_EXPIRY_SECONDS);
      setStep(2);
      setOtpMessage({
        show: true,
        text: `OTP sent to ${formData.email}. Please check your inbox.`,
        type: 'success',
      });
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      toast.error(apiMessage || 'Failed to send OTP. Please try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (!formData.otp || formData.otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    if (otpTimer === 0) {
      setOtpMessage({
        show: true,
        text: 'OTP has expired. Please request a new OTP.',
        type: 'danger',
      });
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const response = await client.get(
        `/api/otp/validate-otp?emailID=${encodeURIComponent(formData.email.trim())}&otp=${encodeURIComponent(formData.otp)}`,
      );
      const { success, message, data } = response.data || {};

      if (!success || !data || data.otpStatus !== 'SUCCESS') {
        setOtpMessage({
          show: true,
          text: message || 'Invalid OTP. Please try again.',
          type: 'danger',
        });
        return;
      }

      setOtpVerified(true);
      setStep(3);
      setOtpMessage({ show: false, text: '', type: 'info' });
      toast.success('OTP verified successfully');
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setOtpMessage({
        show: true,
        text: apiMessage || 'Invalid OTP. Please try again.',
        type: 'danger',
      });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOTP = async () => {
    setIsSendingOtp(true);
    setOtpMessage({ show: false, text: '', type: 'info' });
    try {
      const response = await client.get(
        `/api/otp/resend-otp?emailID=${encodeURIComponent(formData.email.trim())}`,
      );

      if (typeof response.data === 'string') {
        toast.success(response.data || 'OTP resent successfully');
      } else {
        const { success, message, data } = response.data || {};
        if (!success || data?.userID === 0) {
          toast.error(data?.message || message || 'Failed to resend OTP');
          return;
        }
        toast.success(message || data?.message || 'OTP resent successfully');
      }

      setOtpSent(true);
      setOtpVerified(false);
      setOtpTimer(OTP_EXPIRY_SECONDS);
      setFormData((prev) => ({ ...prev, otp: '' }));
      setOtpMessage({
        show: true,
        text: `New OTP sent to ${formData.email}. Please check your inbox.`,
        type: 'success',
      });
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      toast.error(apiMessage || 'Failed to resend OTP. Please try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!otpVerified) {
      toast.error('Please verify OTP before resetting password.');
      return;
    }

    if (!formData.newPassword || !formData.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (formData.newPassword.length < 6 || formData.newPassword.length > 20) {
      toast.error('Password must be between 6 and 20 characters');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsResetting(true);
    try {
      const response = await client.post('/api/users/forgot-password/reset', {
        emailID: formData.email.trim(),
        newPassword: formData.newPassword,
      });
      const { success, message } = response.data || {};

      if (!success) {
        toast.error(message || 'Unable to reset password.');
        return;
      }

      toast.success(message || 'Password reset successfully! Please login with your new password.');
      navigate('/login');
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      toast.error(apiMessage || 'Unable to reset password. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setFormData((prev) => ({ ...prev, otp: '' }));
      setOtpSent(false);
      setOtpVerified(false);
      setOtpTimer(OTP_EXPIRY_SECONDS);
      setOtpMessage({ show: false, text: '', type: 'info' });
    } else if (step === 3) {
      setStep(2);
      setOtpVerified(false);
      setFormData((prev) => ({ ...prev, newPassword: '', confirmPassword: '' }));
    }
  };

  const getLoginPath = () => '/login';

  return (
    <div className="App">
      <Header />

      <section className="login-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8 col-12">
              <div className="login-card">
                <div className="login-header text-center mb-4">
                  <h2 className="login-title">Forgot Password</h2>
                  <p className="login-subtitle">
                    {step === 1 && 'Enter your email to receive an OTP'}
                    {step === 2 && 'Enter the OTP sent to your email'}
                    {step === 3 && 'Create a new password for your account'}
                  </p>
                </div>

                {step === 1 && (
                  <form className="login-form" onSubmit={handleSendOTP}>
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">
                        Email <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaEnvelope />
                        </span>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter email"
                          required
                        />
                      </div>
                    </div>

                    <div className="d-grid gap-2 mb-3">
                      <button type="submit" className="btn login-submit-btn" disabled={isSendingOtp}>
                        <FaKey className="me-2" />
                        {isSendingOtp ? 'Sending…' : 'Send OTP'}
                      </button>
                    </div>

                    <div className="text-center">
                      <Link to={getLoginPath()} className="text-decoration-none">
                        <FaArrowLeft className="me-1" />
                        Back to Login
                      </Link>
                    </div>
                  </form>
                )}

                {step === 2 && (
                  <form className="login-form" onSubmit={handleVerifyOTP}>
                    <div className="mb-3">
                      <label htmlFor="otp" className="form-label">
                        Enter OTP <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaKey />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          id="otp"
                          name="otp"
                          value={formData.otp}
                          onChange={handleChange}
                          placeholder="Enter 6-digit OTP"
                          maxLength="6"
                          inputMode="numeric"
                          pattern="[0-9]{6}"
                          required
                        />
                      </div>
                      {otpTimer > 0 && (
                        <small className="form-text text-muted">
                          OTP expires in: {formatTimer(otpTimer)}
                        </small>
                      )}
                      {otpTimer === 0 && (
                        <small className="form-text text-danger">
                          OTP expired. Please request a new one.
                        </small>
                      )}
                    </div>

                    {otpMessage.show && (
                      <div
                        className={`alert alert-${otpMessage.type === 'danger' ? 'danger' : 'success'} mb-3`}
                        role="alert"
                      >
                        {otpMessage.text}
                      </div>
                    )}

                    <div className="d-grid gap-2 mb-3">
                      <button type="submit" className="btn login-submit-btn" disabled={isVerifyingOtp}>
                        {isVerifyingOtp ? 'Verifying…' : 'Verify OTP'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleResendOTP}
                        disabled={isSendingOtp}
                      >
                        {isSendingOtp ? 'Resending…' : 'Resend OTP'}
                      </button>
                    </div>

                    <div className="text-center">
                      <button
                        type="button"
                        className="btn btn-link text-decoration-none p-0"
                        onClick={handleBack}
                      >
                        <FaArrowLeft className="me-1" />
                        Back
                      </button>
                    </div>
                  </form>
                )}

                {step === 3 && (
                  <form className="login-form" onSubmit={handleResetPassword}>
                    <div className="mb-3">
                      <label htmlFor="newPassword" className="form-label">
                        New Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaLock />
                        </span>
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          className="form-control"
                          id="newPassword"
                          name="newPassword"
                          value={formData.newPassword}
                          onChange={handleChange}
                          placeholder="Enter new password (6-20 characters)"
                          minLength="6"
                          maxLength="20"
                          required
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      <small className="form-text text-muted">Password must be 6-20 characters long</small>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="confirmPassword" className="form-label">
                        Confirm Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaLock />
                        </span>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          className={`form-control ${
                            formData.confirmPassword && formData.newPassword !== formData.confirmPassword
                              ? 'is-invalid'
                              : formData.confirmPassword && formData.newPassword === formData.confirmPassword
                                ? 'is-valid'
                                : ''
                          }`}
                          id="confirmPassword"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Re-enter new password"
                          minLength="6"
                          maxLength="20"
                          required
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                        <div className="invalid-feedback d-block">Passwords do not match</div>
                      )}
                    </div>

                    <div className="d-grid gap-2 mb-3">
                      <button type="submit" className="btn login-submit-btn" disabled={isResetting}>
                        <FaKey className="me-2" />
                        {isResetting ? 'Resetting…' : 'Reset Password'}
                      </button>
                    </div>

                    <div className="text-center">
                      <button
                        type="button"
                        className="btn btn-link text-decoration-none p-0"
                        onClick={handleBack}
                      >
                        <FaArrowLeft className="me-1" />
                        Back
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default ForgotPassword;
